"use client"

import { useState } from "react"
import { useCodexState } from "@/hooks/use-codex-state"
import { CodexTabNavigation } from "../../codex/codex-tab-navigation"
import { CodexMobileDetailSheet } from "../../codex/codex-mobile-detail-sheet"
import { TowersCodexContent } from "../../codex/towers/towers-codex-content"
import { EnemiesCodexContent } from "../../codex/enemies/enemies-codex-content"
import { ItemsCodexContent } from "../../codex/items/items-codex-content"

/**
 * The codex, embedded as a menu section.
 *
 * Reuses the existing tab + thumbnail + details components rather than
 * duplicating them; only the outer chrome differs, since the menu already
 * supplies the sheet, heading and (on mobile) the back button.
 */
export const EncyclopelfiaSection = ({ isNarrow }: { isNarrow: boolean }) => {
  const { activeTab, selectedEntity, setActiveTab, selectEntity, isEntityUnlocked } =
    useCodexState()
  const [isDetailOpen, setIsDetailOpen] = useState(false)

  const select = (id: string | null) => {
    selectEntity(id)
    setIsDetailOpen(id !== null)
  }

  const content =
    activeTab === "towers"
      ? TowersCodexContent({
          selectedTowerId: selectedEntity,
          onSelectTower: select,
          isEntityUnlocked: (id) => isEntityUnlocked(id, "towers"),
        })
      : activeTab === "enemies"
        ? EnemiesCodexContent({
            selectedEnemyId: selectedEntity,
            onSelectEnemy: select,
            isEntityUnlocked: (id) => isEntityUnlocked(id, "enemies"),
          })
        : ItemsCodexContent({
            selectedItemId: selectedEntity,
            onSelectItem: select,
            isEntityUnlocked: (id) => isEntityUnlocked(id, "items"),
          })

  return (
    <div className="flex flex-col h-full min-h-0 -mx-2">
      <div className="rounded-lg overflow-hidden border border-amber-600/20 mb-2">
        <CodexTabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Discovery progress - makes it obvious the locked tiles are earnable
          rather than broken, which a wall of "?" otherwise implies */}
      <div className="flex items-center gap-2 px-1 mb-3">
        <div className="flex-1 h-1.5 rounded-full bg-black/50 overflow-hidden">
          <div
            className="h-full bg-amber-500 transition-[width] duration-300"
            style={{ width: `${content.total > 0 ? (content.discovered / content.total) * 100 : 0}%` }}
          />
        </div>
        <span className="text-[11px] font-bold text-amber-200/80 tabular-nums shrink-0">
          {content.discovered}/{content.total} discovered
        </span>
      </div>

      {isNarrow ? (
        <>
          <div className="grid grid-cols-4 gap-3">{content.thumbnailsContent}</div>
          <CodexMobileDetailSheet isOpen={isDetailOpen} onClose={() => select(null)}>
            {content.detailsContent}
          </CodexMobileDetailSheet>
        </>
      ) : (
        <div className="flex-1 min-h-0 flex gap-4">
          <div
            className="w-[45%] overflow-y-auto pr-1"
            style={{ scrollbarWidth: "thin", scrollbarColor: "#92400e #1e293b" }}
          >
            <div className="grid grid-cols-4 gap-3">{content.thumbnailsContent}</div>
          </div>
          <div
            className="flex-1 overflow-y-auto border-l border-amber-600/20 pl-4"
            style={{ scrollbarWidth: "thin", scrollbarColor: "#92400e #1e293b" }}
          >
            {content.detailsContent ?? (
              <p className="text-gray-500 text-sm text-center pt-8">
                Select an entry to view details
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
